import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import { appUrl, env } from '@astro/config'
import { useCartStore } from '@astro/store'
import { useToast } from '../context/ToastContext.jsx'
import { toList as toAddressList } from '../components/sections/AddressBook.jsx'
import ShipmentTracking from '../components/orders/ShipmentTracking.jsx'
import CancelOrderModal from '../components/orders/CancelOrderModal.jsx'
import { refreshIntervalFor } from '../components/orders/tracking/trackingUtils'
import { currencyFormatter } from '../components/sections/helper'

/** Decimal-string rupees ("2100.00") → formatted INR; em-dash when absent. */
const inr = (v) => currencyFormatter(parseFloat(v)) ?? '—'
const money = (v) => {
  const n = parseFloat(v)
  return Number.isFinite(n) ? n : 0
}

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null
const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
    })
    : null

/** Canonical pipeline stages — labels/icons for the API's status_history keys. */
const STEP_META = {
  pending: {
    label: 'Order Placed',
    icon: <><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" /><path d="M3 6h18" /><path d="M16 10a4 4 0 0 1-8 0" /></>,
  },
  processing: {
    label: 'Processing',
    icon: <><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" /></>,
  },
  shipped: {
    label: 'Shipped',
    icon: <><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" /><path d="M15 18H9" /><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" /><circle cx="17" cy="18" r="2" /><circle cx="7" cy="18" r="2" /></>,
  },
  delivered: {
    label: 'Delivered',
    icon: <><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></>,
  },
}

/** Pill colors for order/payment statuses; unknown values get the neutral set. */
const STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-600 border-amber-200',
  processing: 'bg-blue-50 text-blue-600 border-blue-200',
  shipped: 'bg-indigo-50 text-indigo-600 border-indigo-200',
  delivered: 'bg-green-50 text-green-600 border-green-200',
  paid: 'bg-green-50 text-green-600 border-green-200',
  success: 'bg-green-50 text-green-600 border-green-200',
  completed: 'bg-green-50 text-green-600 border-green-200',
  failed: 'bg-red-50 text-red-600 border-red-200',
  cancelled: 'bg-red-50 text-red-600 border-red-200',
  refunded: 'bg-red-50 text-red-600 border-red-200',
}
const pillCls = (status) =>
  STATUS_STYLES[String(status ?? '').toLowerCase()] ?? 'bg-gray-50 text-gray-500 border-gray-200'

const StatusPill = ({ label, status }) => (
  <span className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border text-xs font-bold uppercase tracking-wider ${pillCls(status)}`}>
    <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
    {label ? `${label}: ` : ''}{String(status ?? '—').replace(/_/g, ' ')}
  </span>
)

/** Main image for an order line: is_main media first, then image_url. */
const itemImage = (item) => {
  const p = item.product
  if (!p) return null
  const media = [...(p.media ?? [])].sort(
    (a, b) =>
      Number(b.custom_properties?.is_main === true) - Number(a.custom_properties?.is_main === true) ||
      (a.order_column ?? 0) - (b.order_column ?? 0),
  )
  return media[0]?.original_url || p.image_url || null
}

export default function OrderDetails() {
  const { orderNumber } = useParams()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const addToCart = useCartStore((s) => s.add)
  const [showAddress, setShowAddress] = useState(true)
  const [cancelOpen, setCancelOpen] = useState(false)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['orderTrack', orderNumber],
    queryFn: () => api.dashboard.trackOrder(orderNumber),
    enabled: Boolean(orderNumber),
    retry: false,
    // While a parcel is moving, re-poll (every minute when out for delivery) —
    // the API pulls fresh courier scans from Shiprocket when its copy is stale.
    refetchInterval: (query) => refreshIntervalFor(query.state.data?.data?.shipment),
  })

  // { status, data: { order, status_history, shipment } } — status:false means not found.
  const payload = data?.data && typeof data.data === 'object' ? data.data : {}
  const order = data?.status === false ? null : payload.order ?? null
  const steps = Array.isArray(payload.status_history) ? payload.status_history : []
  const shipment = payload.shipment ?? null
  // The API decides: true until the courier has the parcel.
  const canCancel = payload.can_cancel === true
  // { reason, cancelled_at, cancelled_by: 'admin'|'customer' } once cancelled, else null.
  const cancellation = payload.cancellation ?? null
  const items = order?.items ?? []

  // Same query key as AddressBook/Cart — shares their cache. The track payload
  // only carries address_book_id, so the address facts come from the saved list.
  const { data: addressData } = useQuery({
    queryKey: ['addresses'],
    queryFn: () => api.addresses.list(),
    enabled: Boolean(order?.address_book_id),
    retry: false,
  })
  const address =
    toAddressList(addressData).find((a) => String(a.id) === String(order?.address_book_id)) ?? null

  const cancelled = ['cancelled', 'canceled', 'refunded', 'failed'].includes(
    String(order?.order_status ?? '').toLowerCase(),
  )
  // Fill the tracker up to the furthest reached stage.
  const reachedIdx = steps.reduce((acc, s, i) => (s.completed || s.active ? i : acc), 0)
  const progressPct = steps.length > 1 ? (reachedIdx / (steps.length - 1)) * 100 : 0

  // Mirrors the API's rule: nothing to invoice on a cancelled order or an
  // online payment that never completed; COD is invoiced before payment.
  const invoiceReady =
    !cancelled &&
    (String(order?.payment_method ?? '').toLowerCase() === 'cod' ||
      String(order?.payment_status ?? '').toLowerCase() === 'success')
  // A plain link: the PDF streams over the session cookie with an attachment
  // disposition (same approach as the puja invoice).
  const invoiceHref = order
    ? `${String(env.VITE_API_BASE_URL).replace(/\/$/, '')}${api.dashboard.orderInvoicePath(order.id)}`
    : null

  const gst = money(order?.price_gst)
  const shipping = money(order?.price_shipping)
  // coupon_discount is always 0 on placed orders (the product discount_rate
  // saving is never stored on its own), so derive the discount from the totals
  // - that way the breakdown always adds up to the amount paid.
  const discount = Math.max(
    0,
    Math.round((money(order?.price_subtotal) + gst + shipping - money(order?.total_amount)) * 100) / 100,
  )

  // Re-add every line of this order to the basket and head to checkout.
  const buyAgain = () => {
    let added = 0
    for (const it of items) {
      const paise = Math.round(money(it.product?.total_price ?? it.price) * 100)
      if (!it.product_id || paise <= 0) continue
      addToCart({
        productId: it.product_id,
        title: it.product_name || it.product?.name || 'Product',
        price: { amount: paise, currency: 'INR' },
        qty: Number(it.quantity) || 1,
      })
      added += 1
    }
    if (!added) return showToast('These items are no longer available')
    showToast(`${added} item${added > 1 ? 's' : ''} added back to your basket`)
    navigate('/checkout')
  }

  const cancelOrder = async (reason) => {
    const res = await api.dashboard.cancelOrder(order.order_number, reason)
    showToast(res?.message || 'Order cancelled')
    refetch()
  }

  const helpHref = `https://wa.me/919091840899?text=${encodeURIComponent(
    `Hello, I need help with my order ${order?.order_number ?? orderNumber}`,
  )}`

  if (isLoading) return <OrderSkeleton />

  if (isError || !order) {
    return (
      <div className="flex flex-col min-h-screen bg-background items-center justify-center px-6 pt-28 pb-20 text-center uppercase select-none">
        <i className="fa-solid fa-box-open text-5xl text-primary/20"></i>
        <h1 className="mt-6 text-3xl font-heading text-primary">Order not found</h1>
        <p className="mt-3 text-base text-gray-500 font-body max-w-md normal-case">
          We couldn't locate order “{orderNumber}”. It may belong to another account, or the link is incorrect.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center justify-center gap-2 px-8 h-12 rounded-xl border border-primary/25 text-primary text-xs font-bold tracking-[0.2em] uppercase hover:border-secondary hover:text-secondary transition-all"
          >
            <i className="fa-solid fa-rotate-right"></i> Try Again
          </button>
          <button
            type="button"
            onClick={() => window.location.assign(appUrl('website', '/dashboard?tab=orders'))}
            className="inline-flex items-center justify-center gap-2 px-8 h-12 rounded-xl bg-primary text-white text-xs font-bold tracking-[0.2em] uppercase hover:bg-secondary transition-all"
          >
            <i className="fa-solid fa-arrow-left"></i> My Orders
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background font-normal uppercase select-none">
      <div className="container max-w-5xl px-6 mx-auto pt-28 md:pt-32 pb-16 space-y-6">
        {/* Back + heading */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => window.location.assign(appUrl('website', '/dashboard?tab=orders'))}
            className="group flex items-center gap-2 text-primary/40 uppercase tracking-widest text-xs hover:text-primary transition-colors"
          >
            <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"></path></svg>
            BACK TO MY ORDERS
          </button>
          <span className="text-[10px] font-mono text-primary/30 tracking-[0.3em]">ORDER.TRACKING.PROTOCOL</span>
        </div>

        {/* Fulfilment tracker */}
        {cancelled ? (
          <div className="bg-white rounded-3xl border border-red-200 shadow-sm px-8 py-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center text-red-500">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6" /><path d="m9 9 6 6" /></svg>
            </div>
            <div>
              <p className="text-lg font-heading text-red-600">This order was {String(order.order_status).replace(/_/g, ' ')}</p>
              {cancellation?.reason && (
                <p className="text-sm text-primary/70 tracking-wider mt-1 normal-case">
                  <span className="font-semibold">
                    {cancellation.cancelled_by === 'customer' ? 'Your reason' : 'Reason from our team'}:
                  </span>{' '}
                  {cancellation.reason}
                </p>
              )}
              <p className="text-sm text-primary/40 tracking-wider mt-1 normal-case">
                Contact support if you were charged — refunds reflect in 5–7 working days.
              </p>
            </div>
          </div>
        ) : steps.length > 0 && (
          <div className="bg-white rounded-3xl border border-border/40 shadow-sm px-6 sm:px-10 pt-8 pb-6 overflow-x-auto">
            <div className="relative min-w-[520px]">
              {/* Track + fill, drawn behind the step circles */}
              <div className="absolute top-6 left-6 right-6 h-1 rounded-full bg-gray-100"></div>
              <div
                className="absolute top-6 left-6 h-1 rounded-full bg-secondary transition-all duration-700"
                style={{ width: `calc(${progressPct} * (100% - 3rem) / 100)` }}
              ></div>
              <div className="relative flex justify-between">
                {steps.map((s, i) => {
                  const meta = STEP_META[String(s.status).toLowerCase()] ?? { label: s.status, icon: STEP_META.pending.icon }
                  const reached = s.completed || s.active
                  return (
                    <div key={s.status} className="flex flex-col items-center text-center w-28">
                      <div
                        className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-colors ${
                          reached
                            ? 'bg-secondary border-secondary text-white shadow-lg shadow-secondary/20'
                            : 'bg-white border-gray-200 text-gray-300'
                        }`}
                      >
                        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{meta.icon}</svg>
                      </div>
                      <p className={`mt-3 text-xs font-bold tracking-wider ${reached ? 'text-primary' : 'text-primary/30'} ${s.active && i === reachedIdx ? 'text-secondary' : ''}`}>
                        {meta.label}
                      </p>
                      <p className="text-[11px] font-mono text-primary/30 mt-1 normal-case">{fmtDate(s.date) ?? ''}</p>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}

        {/* Live courier tracking (Shiprocket) */}
        {!cancelled && (
          <ShipmentTracking
            shipment={shipment}
            orderStatus={order.order_status}
            onNdrRespond={(body) => api.dashboard.ndrResponse(order.order_number, body)}
            onRefresh={() => refetch()}
          />
        )}

        {/* Order card */}
        <div className="bg-white rounded-3xl border border-border/40 shadow-sm overflow-hidden">
          <div className="px-6 sm:px-8 py-6 border-b border-dashed border-border/60 flex flex-wrap items-center gap-x-4 gap-y-3">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-heading text-primary normal-case">
                Order <span className="font-mono text-secondary">#{order.order_number}</span>
              </h1>
              <p className="text-xs text-primary/40 tracking-widest mt-1.5 flex items-center gap-2">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
                Placed on {fmtDateTime(order.created_at) ?? '—'}
              </p>
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <StatusPill status={order.order_status} />
              <StatusPill label="Payment" status={order.payment_status} />
            </div>
          </div>

          {/* Items */}
          <div className="divide-y divide-border/60">
            {items.map((it) => {
              const img = itemImage(it)
              const slug = it.product?.slug
              const nameEl = (
                <h3 className="text-lg sm:text-xl font-heading text-primary normal-case line-clamp-2">
                  {it.product_name || it.product?.name || 'Product'}
                </h3>
              )
              return (
                <div key={it.id} className="px-6 sm:px-8 py-5 flex flex-col sm:flex-row gap-5 sm:items-center">
                  <div className="w-24 h-24 rounded-2xl overflow-hidden border border-border bg-white shrink-0">
                    {img ? (
                      <img src={img} alt={it.product_name ?? ''} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-primary/20">
                        <i className="fa-solid fa-gem text-2xl"></i>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 space-y-1.5">
                    {slug ? (
                      <Link to={`/products/${slug}`} className="hover:text-secondary transition-colors">{nameEl}</Link>
                    ) : nameEl}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs tracking-wider text-primary/40">
                      {it.product?.sku && <span>SKU: <span className="font-mono text-primary/60">{it.product.sku}</span></span>}
                      {it.variation_name && <span>Variant: <span className="text-primary/60">{it.variation_name}</span></span>}
                      {parseFloat(it.carat) > 0 && <span>Carat: <span className="text-primary/60">{it.carat}</span></span>}
                    </div>
                    <p className="text-sm text-primary/40 tracking-wider">
                      Qty: <span className="font-mono text-primary">{it.quantity}</span>
                      <span className="mx-2 text-primary/20">×</span>
                      <span className="font-mono text-primary">{inr(it.price)}</span>
                    </p>
                  </div>
                  <p className="text-xl font-heading text-primary shrink-0 sm:text-right">{inr(it.subtotal)}</p>
                </div>
              )
            })}
          </div>

          {/* Actions */}
          <div className="px-6 sm:px-8 py-5 border-t border-dashed border-border/60 bg-muted/10 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={buyAgain}
              className="inline-flex items-center gap-2 h-12 px-7 rounded-xl bg-secondary text-white text-xs font-bold tracking-[0.15em] uppercase hover:bg-primary transition-all active:scale-95"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /><path d="M8 16H3v5" /></svg>
              Buy Again
            </button>
            <a
              href={helpHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 h-12 px-7 rounded-xl border border-primary/20 text-primary text-xs font-bold tracking-[0.15em] uppercase hover:border-secondary hover:text-secondary transition-all"
            >
              <i className="fa-brands fa-whatsapp text-base"></i>
              Need Help?
            </a>
            {invoiceReady && (
              <a
                href={invoiceHref}
                download
                className="inline-flex items-center gap-2 h-12 px-7 rounded-xl border border-primary/20 text-primary text-xs font-bold tracking-[0.15em] uppercase hover:border-secondary hover:text-secondary transition-all"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 15V3" /><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="m7 10 5 5 5-5" /></svg>
                Download Invoice
              </a>
            )}
            {canCancel && (
              <button
                type="button"
                onClick={() => setCancelOpen(true)}
                className="inline-flex items-center gap-2 h-12 px-7 rounded-xl border border-red-200 text-red-600 text-xs font-bold tracking-[0.15em] uppercase hover:bg-red-50 hover:border-red-300 transition-all sm:ml-auto"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6" /><path d="m9 9 6 6" /></svg>
                Cancel Order
              </button>
            )}
          </div>
          <CancelOrderModal
            open={cancelOpen}
            onClose={() => setCancelOpen(false)}
            onSubmit={cancelOrder}
            paid={String(order.payment_status ?? '').toLowerCase() === 'success'}
          />
        </div>

        {/* Payment + delivery details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-3xl border border-border/40 shadow-sm p-6 sm:p-8 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-secondary/10 border border-secondary/20 flex items-center justify-center text-secondary">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect width="20" height="14" x="2" y="5" rx="2" /><line x1="2" x2="22" y1="10" y2="10" /></svg>
              </div>
              <h2 className="text-base font-heading text-primary tracking-widest">Payment Details</h2>
            </div>
            <dl className="space-y-3.5 text-sm tracking-wider">
              <FactRow label="Method" value={String(order.payment_method ?? '—').replace(/_/g, ' ')} />
              <FactRow label="Status" value={<StatusPill status={order.payment_status} />} />
              {order.transaction_id && <FactRow label="Transaction ID" value={<span className="font-mono normal-case">{order.transaction_id}</span>} />}
              {order.payment_date && <FactRow label="Paid On" value={fmtDateTime(order.payment_date)} />}
              {order.order_notes && <FactRow label="Order Notes" value={<span className="normal-case">{order.order_notes}</span>} />}
            </dl>
          </div>

          <div className="bg-white rounded-3xl border border-border/40 shadow-sm p-6 sm:p-8 space-y-5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-secondary/10 border border-secondary/20 flex items-center justify-center text-secondary">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" /><circle cx="12" cy="10" r="3" /></svg>
                </div>
                <h2 className="text-base font-heading text-primary tracking-widest">Delivery Address</h2>
              </div>
              {address && (
                <button
                  type="button"
                  onClick={() => setShowAddress((v) => !v)}
                  className="text-xs font-bold tracking-widest text-secondary hover:text-primary transition-colors"
                >
                  {showAddress ? 'HIDE' : 'SHOW'}
                </button>
              )}
            </div>
            {!address ? (
              <p className="text-sm text-primary/40 tracking-wider normal-case">
                Address details are not available for this order.
              </p>
            ) : showAddress ? (
              <div className="space-y-2 text-sm text-primary/70 normal-case leading-relaxed">
                <p className="font-semibold text-primary uppercase text-sm tracking-widest flex items-center gap-2">
                  {address.name || [address.shipping_first_name, address.shipping_last_name].filter(Boolean).join(' ') || 'Saved address'}
                  {(address.address_type || address.type) && (
                    <span className="px-2.5 py-0.5 rounded-full bg-secondary/10 border border-secondary/20 text-secondary text-[10px] tracking-widest uppercase">
                      {address.address_type || address.type}
                    </span>
                  )}
                </p>
                <p>{address.shipping_address || address.address || address.billing_address}</p>
                {address.landmark && <p>Landmark: {address.landmark}</p>}
                <p>
                  {[address.shipping_city?.name || address.city, address.shipping_state?.name || address.state]
                    .filter(Boolean)
                    .join(', ')}
                  {(address.shipping_zip_code || address.pincode) && ` — ${address.shipping_zip_code || address.pincode}`}
                </p>
                {(address.shipping_phone_number || address.mobile) && (
                  <p className="font-mono text-primary/50">☎ {address.shipping_phone_number || address.mobile}</p>
                )}
              </div>
            ) : (
              <p className="text-xs text-primary/30 tracking-widest">Hidden — tap SHOW to reveal.</p>
            )}
          </div>
        </div>

        {/* Price breakdown */}
        <div className="bg-white rounded-3xl border border-border/40 shadow-sm p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-6">
            <div className="h-1.5 w-1.5 rounded-full bg-secondary animate-pulse"></div>
            <span className="text-xs text-secondary tracking-widest">PRICE BREAKDOWN</span>
          </div>
          <div className="space-y-4 text-base">
            <SummaryRow label="Subtotal" value={inr(order.price_subtotal)} />
            {discount > 0 && (
              <SummaryRow
                label={`Discount${order.coupon_code ? ` (${order.coupon_code})` : ''}`}
                value={`−${inr(discount)}`}
                accent
              />
            )}
            {gst > 0 && <SummaryRow label="GST" value={inr(order.price_gst)} />}
            <SummaryRow label="Shipping" value={shipping > 0 ? inr(order.price_shipping) : 'FREE'} accent={shipping <= 0} />
            <div className="flex justify-between items-center pt-5 border-t border-border">
              <div>
                <span className="font-heading text-primary text-xl block">TOTAL PAID</span>
                <span className="text-[10px] font-mono text-primary/20 tracking-[0.2em]">INCL. OF ALL TAXES</span>
              </div>
              <span className="text-4xl font-heading text-primary">{inr(order.total_amount)}</span>
            </div>
          </div>
        </div>

        {/* Timeline */}
        {steps.length > 0 && (
          <div className="bg-white rounded-3xl border border-border/40 shadow-sm p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-secondary/10 border border-secondary/20 flex items-center justify-center text-secondary">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
              </div>
              <h2 className="text-base font-heading text-primary tracking-widest">Order Timeline</h2>
            </div>
            <ol className="relative border-l-2 border-border/60 ml-5 space-y-8">
              {steps.map((s) => {
                const meta = STEP_META[String(s.status).toLowerCase()] ?? { label: s.status }
                const reached = s.completed || s.active
                return (
                  <li key={s.status} className="pl-6 relative">
                    <span
                      className={`absolute -left-[8px] top-1 w-3.5 h-3.5 rounded-full border-2 ${
                        s.active ? 'bg-secondary border-secondary animate-pulse' : reached ? 'bg-secondary border-secondary' : 'bg-white border-gray-300'
                      }`}
                    ></span>
                    <p className={`text-sm font-bold tracking-wider ${reached ? 'text-primary' : 'text-primary/30'}`}>
                      {meta.label}
                      {s.active && <span className="ml-2 text-[10px] text-secondary">● CURRENT</span>}
                    </p>
                    <p className="text-xs font-mono text-primary/30 mt-1 normal-case">
                      {fmtDateTime(s.date) ?? 'Awaiting update'}
                    </p>
                  </li>
                )
              })}
            </ol>
          </div>
        )}
      </div>
    </div>
  )
}

function FactRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-primary/40 shrink-0">{label}</dt>
      <dd className="text-primary font-medium text-right min-w-0 break-words">{value}</dd>
    </div>
  )
}

function SummaryRow({ label, value, accent = false }) {
  return (
    <div className="flex justify-between items-center font-body">
      <span className="text-primary/40 uppercase tracking-widest text-xs">{label}</span>
      <span className={accent ? 'text-secondary font-medium' : 'text-primary font-medium'}>{value}</span>
    </div>
  )
}

function OrderSkeleton() {
  return (
    <div className="min-h-screen bg-background select-none">
      <div className="container max-w-5xl px-6 mx-auto pt-28 md:pt-32 pb-16 space-y-6 animate-pulse">
        <div className="h-4 w-48 rounded bg-gray-200" />
        <div className="bg-white rounded-3xl border border-border/40 p-8">
          <div className="flex justify-between">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gray-200" />
                <div className="h-3 w-20 rounded bg-gray-100" />
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-3xl border border-border/40 p-8 space-y-6">
          <div className="h-7 w-80 rounded bg-gray-200" />
          <div className="flex gap-5">
            <div className="w-24 h-24 rounded-2xl bg-gray-200 shrink-0" />
            <div className="flex-1 space-y-3">
              <div className="h-5 w-2/3 rounded bg-gray-200" />
              <div className="h-4 w-1/3 rounded bg-gray-100" />
              <div className="h-4 w-1/4 rounded bg-gray-100" />
            </div>
          </div>
          <div className="flex gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-12 w-40 rounded-xl bg-gray-100" />
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-48 bg-white rounded-3xl border border-border/40" />
          <div className="h-48 bg-white rounded-3xl border border-border/40" />
        </div>
      </div>
    </div>
  )
}
